import { saveDiceTruth, publishSavedDice } from '../shared/dice-publication';
import { readItemField } from '../janus-engine/compile/item-field-reader';
// The native resolvers decide the roll; PHP stores it and this browser delivers the saved rows.
// Lost answers retry the same operation id. There is no optimistic log insertion.
import { resolveBareRoll, resolveComplexRoll, ROLL_SCHEMA, type BareRollInput } from '../janus-engine/rolls/dice-roll.js';
import { RANDOM_TABLE_KINDS, readRandomTableDef, rollRandomTable, type RandomTableDef } from '../janus-engine/rolls/random-table.js';
import { selectRollReveal, prepareTableRoll } from '../janus-engine/rolls/roll-commit-frame';
import { heldRollRows } from './roll-update';
import { withLogParticipants } from '../shared/log-participant-snapshot';

type Row = Record<string, unknown>;
const ENDPOINT = '/options/game/actions/roll_truth_commit_inc.php';
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const uuidOrEmpty = (v: unknown): string => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(upper(v)) ? upper(v) : '';
const privacy = (v: unknown): number => [0, 1, 2].includes(Number(v)) ? Number(v) : 0;
const warn = (message: string): void => { if (typeof displayWarning === 'function') displayWarning(message); };

export type RollCommitOutcome = 'handled' | null;
export interface RollContext { campaign_id?: unknown; encounter_id?: unknown; caster_id?: unknown; roll_private?: unknown }

/** POST one operation; a lost answer retries the SAME operation id (the commit is idempotent on it). */
async function commit(body: Row, label: string): Promise<Row | null> {
  body = withLogParticipants(body);
  try {
    const { status, result } = await saveDiceTruth(ENDPOINT, body as Row & { operation_id: string });
    if (status === 200 && result.ok === true) {
      const scope = (result.roll_updates as Row[] | undefined)?.[0];
      const delivery = scope ? { ...body, encounter_id: scope.encounter_id, campaign_id: scope.campaign_id, actor_id: '' } : body;
      publishSavedDice(delivery as Row & { operation_id: string }, result);
      window.dispatchEvent(new CustomEvent('ft:roll-truth-committed', { detail: { body, result } }));
      return result;
    }
    warn(status === 403 ? `You do not have permission for that ${label}.` : `The ${label} was refused: ${String(result.error ?? status)}`);
    window.dispatchEvent(new CustomEvent('ft:roll-truth-refused', { detail: { body, status, result } }));
  } catch (error) { warn(`The ${label} could not be confirmed (${String(error)}). Check the log before rolling again.`); }
  return null;
}

/** Free roll. Null asks the caller to display its input refusal. */
export function tryCommitBareRoll(input: BareRollInput, ctx: RollContext): RollCommitOutcome {
  // A random-table kind is a TABLE roll (Lane N2 RT-3): the browser rolls the table; roll_inc.php's table branch is gone.
  if (RANDOM_TABLE_KINDS.has(String(input.roll_type_1 ?? '')) && uuidOrEmpty(input.roll_item_id)) {
    void tryCommitTableRoll(input.roll_item_id, ctx);
    return 'handled';
  }
  const itemId = String(input.roll_item_id ?? '');
  if (itemId !== '' && !uuidOrEmpty(itemId)) return null;
  if (String(input.roll_type_1 ?? '').length > 64 || String(input.roll_type_2 ?? '').length > 64) return null;
  const roll = resolveBareRoll(input);
  if (!roll) return null;
  const body = {
    schema: ROLL_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), kind: 'dice', roll_private: privacy(ctx.roll_private),
    campaign_id: uuidOrEmpty(ctx.campaign_id), encounter_id: uuidOrEmpty(ctx.encounter_id), caster_id: uuidOrEmpty(ctx.caster_id),
    roll_type_1: String(input.roll_type_1 ?? ''), roll_type_2: String(input.roll_type_2 ?? ''), roll_item_id: itemId ? upper(itemId) : '',
    dice: roll.dice, die: roll.die, roll_mod: roll.roll_mod, adv_dis_stacks: roll.adv_dis_stacks, faces: roll.faces,
    roll_result: roll.roll_result, rolls_full: roll.rolls_full,
  };
  void commit(body, 'roll');
  return 'handled';
}

/** Multi-die tray. Null asks the caller to display its input refusal. */
export function tryCommitComplexRoll(qty: Record<number, unknown>, modRaw: unknown, advRaw: unknown, ctx: RollContext): RollCommitOutcome {
  const roll = resolveComplexRoll(qty, modRaw, advRaw);
  if (!roll) return null;
  const body = {
    schema: ROLL_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), kind: 'complex', roll_private: privacy(ctx.roll_private),
    campaign_id: uuidOrEmpty(ctx.campaign_id), encounter_id: uuidOrEmpty(ctx.encounter_id), caster_id: uuidOrEmpty(ctx.caster_id),
    dice: roll.dice, die: roll.die, counts: roll.counts, roll_mod: roll.mod, adv_dis_stacks: roll.adv, faces: roll.faces, roll_result: roll.roll_result, rolls_full: roll.rolls_full,
  };
  void commit(body, 'roll');
  return 'handled';
}

/** GM reveal, authorized from the persisted roll. Null means the roll id is invalid. */
export function tryCommitRollReveal(rollId: unknown): RollCommitOutcome {
  const id = uuidOrEmpty(rollId);
  if (!id) return null;
  const row = selectRollReveal(heldRollRows(), id);
  if (!row) { warn('There is no held hidden roll row to reveal.'); return null; }
  void commit({ schema: ROLL_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), kind: 'reveal', roll_id: id, roll_i: Number(row.roll_i) }, 'reveal');
  return 'handled';
}

// Tables load through the existing object batch before the resolver spends entropy.
const isObj = (v: unknown): v is Row => !!v && typeof v === 'object' && !Array.isArray(v);
function tableIn(root: unknown, key: string, requireRanges = true): Row | null {
  if (!isObj(root)) return null;
  if (isObj(root.items) && upper(readItemField(root, 'item_id')) === key && (!requireRanges || Array.isArray(root.random_table_ranges))) return root;
  const fromCatalog = (cat: unknown): Row | null => {
    if (!isObj(cat)) return null;
    for (const k of [key, key.toLowerCase()]) { const e = cat[k]; if (isObj(e) && (!requireRanges || Array.isArray(e.random_table_ranges))) return e; }
    return null;
  };
  const own = fromCatalog(root.catalog);
  if (own) return own;
  for (const member of Array.isArray(root.encounter_creatures_arr) ? root.encounter_creatures_arr : []) {
    const hit = isObj(member) ? fromCatalog(member.catalog) : null;
    if (hit) return hit;
  }
  return null;
}
/** The table definition (with its bucket) any cache on the page holds, or null. */
export function heldRandomTableDef(tableId: unknown, requireRanges = true): Row | null {
  const key = uuidOrEmpty(tableId);
  if (!key) return null;
  const ft = window.FateTide as unknown as Record<string, unknown> | undefined;
  for (const name of ['objectCache', 'casterCache', 'encounterCache']) {
    const cache = ft?.[name];
    if (!(cache instanceof Map)) continue;
    for (const value of cache.values()) { const hit = tableIn(value, key, requireRanges); if (hit) return hit; }
  }
  return null;
}
async function fetchRandomTableDef(id: string): Promise<Row | null> {
  try {
    const read = (window as unknown as { ftFetchObjectsBatch?: (ids: string[]) => Promise<void> }).ftFetchObjectsBatch;
    if (!read) return null;
    await read([id]);
    return heldRandomTableDef(id);
  } catch {
    return null;
  }
}
const TABLE_REFUSALS: Record<string, string> = {
  'unsupported:roll_type': 'This table rolls with advantage rules the browser does not resolve yet.',
  'empty-table': 'This table has no rows to roll.',
  'chain-too-long': 'This table chains through too many nested tables.',
};
export interface TableRollContext extends RollContext { target_id?: unknown }
/** Roll a random table and commit it. 'handled' = recorded; 'refused' = a message was shown; null = not a table id (caller decides). */
export async function tryCommitTableRoll(tableId: unknown, ctx: TableRollContext = {}): Promise<'handled' | 'refused' | null> {
  const id = uuidOrEmpty(tableId);
  if (!id) return null;
  // Pin the closure through hydration and resolution, including across awaited reads.
  const pinned = new Map<string, RandomTableDef | null>();
  const defs = (key: string): RandomTableDef | null => {
    if (!pinned.has(key)) { const held = readRandomTableDef(key, heldRandomTableDef(key)); if (!held) return null; pinned.set(key, held); }
    return pinned.get(key)!;
  };
  const refusal = await prepareTableRoll(id, defs, async key => {
    const raw = await fetchRandomTableDef(key);
    const def = raw ? readRandomTableDef(key, raw) : null;
    pinned.set(key, def);
    return def;
  });
  if (refusal) { warn(TABLE_REFUSALS[refusal] ?? `This table cannot be rolled (${refusal}).`); return 'refused'; }
  const out = rollRandomTable(id, defs);
  if (!out.ok) {
    const missing = 'need' in out ? out.need : id;
    const label = readItemField(heldRandomTableDef(missing, false), 'item_name') || missing;
    warn('need' in out ? `Random table "${String(label)}" is unavailable.`
      : TABLE_REFUSALS[out.refuse] ?? `This table cannot be rolled (${out.refuse}).`);
    return 'refused';
  }
  const encounterId = uuidOrEmpty(ctx.encounter_id);
  const body = {
    schema: ROLL_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), kind: 'table', roll_private: privacy(ctx.roll_private),
    campaign_id: uuidOrEmpty(ctx.campaign_id), encounter_id: encounterId, caster_id: uuidOrEmpty(ctx.caster_id),
    target_id: encounterId ? uuidOrEmpty(ctx.target_id) : '', table_id: id,
    steps: out.result.steps.map(s => ({ roll_type_1: s.roll_type_1, roll_type_2: s.roll_type_2, roll_item_id: s.roll_item_id, adv_dis_stacks: s.adv_dis_stacks,
      dice: s.dice, die: s.die, roll_mod: s.roll_mod, pools: s.pools.map(p => ({ dice: p.dice, die: p.die, mod: p.mod, faces: p.faces })), roll_result: s.roll_result, rolls_full: s.rolls_full,
      random_table: s.random_table })),
  };
  return (await commit(body, 'table roll')) ? 'handled' : 'refused';
}
/** A table's own roll button (parse-text `[random_table:…]`, the item page): the sidelog's context, the first selected creature as
 *  the log's "to". Falls back to useAbility() only when the id is not a UUID. */
export async function rollRandomTableItem(tableId: unknown): Promise<void> {
  if (typeof openSideLog === 'function') { try { openSideLog(); } catch { /* optional panel */ } }
  const sl = typeof getSidelogContext === 'function' ? getSidelogContext() : null;
  const firstTarget = String(sl?.target_id ?? '').split(/[,\s]+/).map(uuidOrEmpty).find(Boolean) ?? '';
  const outcome = await tryCommitTableRoll(tableId, { campaign_id: sl?.campaign_id, encounter_id: sl?.encounter_id, caster_id: sl?.caster_id,
    roll_private: sl?.roll_private, target_id: firstTarget });
  if (outcome === null && typeof (window as unknown as { useAbility?: (id: unknown) => unknown }).useAbility === 'function') {
    (window as unknown as { useAbility: (id: unknown) => unknown }).useAbility(tableId);
  }
}

Object.assign(window, { ftTryCommitBareRoll: tryCommitBareRoll, ftTryCommitComplexRoll: tryCommitComplexRoll, ftTryCommitRollReveal: tryCommitRollReveal,
  ftTryCommitTableRoll: tryCommitTableRoll, ftRollRandomTableItem: rollRandomTableItem, ftHeldRandomTableDef: heldRandomTableDef });
